import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { sessionService } from '@/services/session-service';

import { useSession } from '@/lib/auth-client';

interface UserSession {
  id: string;
  userId: string;
  expiresAt: string;
  createdAt: string;
  ipAddress?: string;
  userAgent?: string;
  location?: string;
  current?: boolean; // Flag to indicate if this is the current session
}

interface SessionManagementProps {}

const UserSessionManagement: React.FC<SessionManagementProps> = () => {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const { data: currentSession } = useSession();

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const sessionData = await sessionService.getSessions();
      setSessions(sessionData);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    if (currentSession?.session?.id === sessionId) {
      toast.error('Cannot revoke your current session from here');
      return;
    }

    try {
      await sessionService.revokeSession(sessionId);
      toast.success('Session revoked successfully');
      loadSessions(); // Refresh the list
    } catch (error) {
      console.error('Error revoking session:', error);
      toast.error('Failed to revoke session');
    }
  };

  const handleRevokeAllOtherSessions = async () => {
    try {
      await sessionService.revokeAllOtherSessions();
      toast.success('All other sessions revoked successfully');
      loadSessions(); // Refresh the list
    } catch (error) {
      console.error('Error revoking other sessions:', error);
      toast.error('Failed to revoke other sessions');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <p>Loading sessions...</p>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Session Management
        </CardTitle>
        <CardDescription>
          Manage your active login sessions across different devices
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button 
              variant="destructive" 
              onClick={handleRevokeAllOtherSessions}
              disabled={sessions.filter(s => !s.current).length === 0}
            >
              Revoke All Other Sessions
            </Button>
          </div>
          
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Device</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.length > 0 ? (
                  sessions.map((session) => (
                    <TableRow key={session.id} className={session.current ? 'bg-muted/30' : ''}>
                      <TableCell className="font-medium">
                        {session.current ? (
                          <span className="flex items-center">
                            {session.userAgent || 'Current Session'} <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Current</span>
                          </span>
                        ) : (
                          session.userAgent || 'Unknown Device'
                        )}
                      </TableCell>
                      <TableCell>{session.ipAddress || 'N/A'}</TableCell>
                      <TableCell>{session.location || 'Unknown'}</TableCell>
                      <TableCell>{session.createdAt ? new Date(session.createdAt).toLocaleString() : 'N/A'}</TableCell>
                      <TableCell>{session.expiresAt ? new Date(session.expiresAt).toLocaleString() : 'N/A'}</TableCell>
                      <TableCell>
                        {session.current ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                            Active
                          </span>
                        ) : session.expiresAt && new Date(session.expiresAt) < new Date() ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-800">
                            Expired
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                            Active
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {session.current ? (
                          <span className="text-muted-foreground text-sm">Current session</span>
                        ) : (
                          <Button 
                            variant="outline" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRevokeSession(session.id)}
                          >
                            Revoke
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No active sessions
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="text-sm text-muted-foreground">
            <p>Note: You cannot revoke your current session from this interface. To log out of this device, use the logout button in the top right corner.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default UserSessionManagement;